/**
 * Bridge to the Tauri shell.
 *
 * The same bundle ships to browsers, so every call degrades to a no-op (or to
 * `localStorage`) when the shell is absent. Nothing in the app should branch on
 * the platform beyond calling through this module.
 */

type InvokeFn = <T>(command: string, args?: Record<string, unknown>) => Promise<T>;
type ListenFn = (
  event: string,
  handler: (payload: { payload: unknown }) => void,
) => Promise<() => void>;

interface TauriInternals {
  invoke: InvokeFn;
}

export const isDesktop = '__TAURI_INTERNALS__' in globalThis;

function internals(): TauriInternals | null {
  if (!isDesktop) return null;
  return (globalThis as unknown as { __TAURI_INTERNALS__: TauriInternals })
    .__TAURI_INTERNALS__;
}

async function invoke<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T | null> {
  const bridge = internals();
  if (!bridge) return null;
  try {
    return await bridge.invoke<T>(command, args);
  } catch (error) {
    console.warn(`desktop command ${command} failed`, error);
    return null;
  }
}

export interface CachedEntry<T> {
  key: string;
  payload: T;
  updated_at: number;
}

export interface QueuedChange {
  id: number;
  entity_type: string;
  entity_id: string;
  operation: string;
  payload: unknown;
  created_at: number;
}

/**
 * Offline cache. In the browser this falls back to `localStorage`, which is
 * enough to survive a refresh but not a full offline session.
 */
export const offlineCache = {
  async put(key: string, payload: unknown): Promise<void> {
    if (isDesktop) {
      await invoke('cache_put', { key, payload });
      return;
    }
    try {
      window.localStorage.setItem(
        `4zen.cache.${key}`,
        JSON.stringify({ payload, updated_at: Date.now() / 1000 }),
      );
    } catch {
      // A full quota is not worth breaking the request over.
    }
  },

  async get<T>(key: string): Promise<CachedEntry<T> | null> {
    if (isDesktop) {
      return invoke<CachedEntry<T> | null>('cache_get', { key });
    }
    const raw = window.localStorage.getItem(`4zen.cache.${key}`);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { payload: T; updated_at: number };
      return { key, ...parsed };
    } catch {
      return null;
    }
  },

  async forget(prefix: string): Promise<void> {
    if (isDesktop) {
      await invoke('cache_forget', { prefix });
      return;
    }
    const scoped = `4zen.cache.${prefix}`;
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(scoped))
      .forEach((key) => window.localStorage.removeItem(key));
  },

  async clear(): Promise<void> {
    if (isDesktop) {
      await invoke('cache_clear');
      return;
    }
    await offlineCache.forget('');
  },
};

export const changeQueue = {
  async push(change: {
    entityType: string;
    entityId: string;
    operation: string;
    payload: unknown;
  }): Promise<void> {
    await invoke('queue_change', {
      entityType: change.entityType,
      entityId: change.entityId,
      operation: change.operation,
      payload: change.payload,
    });
  },

  async list(): Promise<QueuedChange[]> {
    return (await invoke<QueuedChange[]>('queued_changes')) ?? [];
  },

  async drop(ids: number[]): Promise<void> {
    if (ids.length === 0) return;
    await invoke('drop_queued', { ids });
  },
};

/** Native toast; falls back to the Web Notification API in the browser. */
export async function notify(title: string, body: string): Promise<void> {
  if (isDesktop) {
    await invoke('notify', { title, body });
    return;
  }
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'granted') {
    new Notification(title, { body });
  } else if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') new Notification(title, { body });
  }
}

export async function setPendingBadge(count: number): Promise<void> {
  await invoke('set_pending_badge', { count });
}

/** Tray menu items emit `tray://action`; the app maps them onto routes. */
export async function onTrayAction(
  handler: (action: string) => void,
): Promise<() => void> {
  if (!isDesktop) return () => {};
  try {
    const { listen } = (await import('@tauri-apps/api/event')) as {
      listen: ListenFn;
    };
    return await listen('tray://action', (event) =>
      handler(String(event.payload)),
    );
  } catch {
    return () => {};
  }
}

/** Checks for a signed update and installs it after the user agrees. */
export async function checkForUpdates(): Promise<string | null> {
  if (!isDesktop) return null;
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) return null;
    await update.downloadAndInstall();
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
    return update.version;
  } catch (error) {
    console.warn('update check failed', error);
    return null;
  }
}
